/* LONGEVX · Hero slider (imágenes con Ken Burns, video con póster / imagen final / último cuadro) */
(() => {
  if (customElements.get('lx-hero')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  class LxHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.lx-hero__slide')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      this.timer = null;
      this.inView = true;
      this.paused = false;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.speed = parseInt(this.dataset.speed || '7000', 10);
      this.style.setProperty('--dot-dur', `${this.speed}ms`);

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.dot, 10), true)));

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
        if (e.key === 'ArrowRight') this.go(this.index + 1, true);
      });

      this.#bindSwipe();
      this.#bindVisibility();
      this.#bindEditor();

      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (video) {
          video.addEventListener('ended', () => this.#onVideoEnd(slide, video));
          if (slide.dataset.end === 'loop_tail') this.#bindCrossLoop(slide, video, true);
          if (slide.dataset.end === 'loop') this.#bindCrossLoop(slide, video, false);
        }
      });

      this.activate(0, true);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.io?.disconnect();
    }

    go(i, user = false) {
      const n = this.slides.length;
      if (n < 2) return;
      const next = ((i % n) + n) % n;
      if (next === this.index) return;
      if (user) this.userInteracted = true;
      this.activate(next);
    }

    activate(i, initial = false) {
      clearTimeout(this.timer);
      const prev = this.slides[this.index];
      if (!initial && prev) this.#deactivate(prev);

      this.index = i;
      const slide = this.slides[i];
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === i);
        s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
      });
      this.dots.forEach((d, k) => {
        d.classList.toggle('is-active', k === i);
        d.setAttribute('aria-selected', k === i ? 'true' : 'false');
        // reinicia la animación de progreso
        const fill = d.querySelector('.lx-hero__dot-fill');
        if (fill && k === i) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      });

      slide.classList.remove('is-ended', 'is-loopfade');
      slide._xf?.reset();
      const type = slide.dataset.type;

      if (type === 'video') {
        this.#playVideo(slide);
      } else {
        this.#startKb(slide.querySelector('[data-kb]'));
        this.#schedule(this.speed);
      }

      // precarga del siguiente
      const nextSlide = this.slides[(i + 1) % this.slides.length];
      if (nextSlide) this.#prepareVideo(nextSlide, false);
    }

    #deactivate(slide) {
      slide._xf?.reset();
      slide.querySelectorAll('.lx-hero__video--twin').forEach((t) => t.pause());
      const video = slide.querySelector('video');
      if (video) {
        video.pause();
        try { video.currentTime = 0; } catch (e) {}
      }
      slide.querySelectorAll('[data-kb], [data-end-image]').forEach((el) => this.#stopKb(el));
      slide.classList.remove('is-ended');
    }

    #schedule(ms) {
      clearTimeout(this.timer);
      if (!this.autoplay || this.paused || !this.inView) return;
      this.timer = setTimeout(() => this.go(this.index + 1), ms);
    }

    #prepareVideo(slide, autoplay) {
      const video = slide.querySelector('video');
      if (!video) return null;
      if (!video.dataset.loaded) {
        if (isMobile() && video.dataset.posterMobile) video.poster = video.dataset.posterMobile;
        const src = (isMobile() && video.dataset.srcMobile) || video.dataset.src;
        if (src) {
          video.src = src;
          video.dataset.loaded = '1';
          video.preload = autoplay ? 'auto' : 'metadata';
        }
      }
      return video;
    }

    #playVideo(slide) {
      const video = this.#prepareVideo(slide, true);
      if (!video || !video.dataset.src) {
        this.#schedule(this.speed);
        return;
      }
      this.#stopKb(slide.querySelector('[data-kb]'));
      // El dot muestra progreso según la duración real
      const setDur = () => {
        if (video.duration && isFinite(video.duration)) {
          this.style.setProperty('--dot-dur', `${Math.round(video.duration * 1000)}ms`);
        }
      };
      if (video.readyState >= 1) setDur();
      else video.addEventListener('loadedmetadata', setDur, { once: true });

      try { video.currentTime = 0; } catch (e) {}
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Sin autoplay (ahorro de batería, etc.): queda el póster con Ken Burns
          slide.classList.add('is-ended');
          this.#startKb(slide.querySelector('[data-end-image]') || slide.querySelector('[data-kb]'));
          this.style.setProperty('--dot-dur', `${this.speed}ms`);
          this.#schedule(this.speed);
        });
      }
      if (slide.dataset.end === 'loop') {
        // en bucle: avanza según el tiempo configurado
        this.style.setProperty('--dot-dur', `${Math.max(this.speed, 8000)}ms`);
        this.#schedule(Math.max(this.speed, 8000));
      }
    }

    /* Bucle con fundido cruzado real: un segundo video arranca desde el punto de bucle
       y se mezcla sobre el primero durante XF segundos. Nunca pasa por negro. */
    #bindCrossLoop(slide, video, tail) {
      // Bucle de tramo final (cuadros coincidentes): mezcla breve justo en el empalme. Bucle completo: mezcla larga.
      const XF = tail ? 0.3 : 1.4;
      const LEAD = tail ? 0.32 : XF + 0.45;
      const start = tail ? parseFloat(String(slide.dataset.loopStart || '').replace(',', '.')) : 0;
      if (!isFinite(start)) return;
      const wrap = video.parentElement;
      const twin = document.createElement('video');
      twin.className = 'lx-hero__video lx-hero__video--twin';
      twin.muted = true; twin.playsInline = true; twin.setAttribute('playsinline', ''); twin.setAttribute('aria-hidden', 'true');
      twin.preload = 'auto';
      twin.style.opacity = '0';
      wrap.appendChild(twin);
      const st = { active: video, idle: twin, fading: false, timer: 0, passes: 0 };
      slide._xf = st;
      const ensureSrc = () => {
        const src = video.currentSrc || video.src;
        if (src && twin.src !== src) twin.src = src;
      };
      video.addEventListener('loadedmetadata', ensureSrc);
      const swap = (a, b) => {
        a.pause();
        a.style.transition = 'none'; a.style.opacity = '0';
        st.active = b; st.idle = a; st.fading = false; st.primed = false;
        st.passes += 1;
        if (tail && st.passes === 1 && slide.classList.contains('is-active')) {
          const dur = Math.max(2000, Math.round((video.duration - start) * 1000));
          this.style.setProperty('--dot-dur', `${dur}ms`);
          this.#restartDot();
          this.#schedule(dur);
        }
      };
      const begin = () => {
        if (st.fading || !slide.classList.contains('is-active')) return;
        st.fading = true;
        ensureSrc();
        const a = st.active; const b = st.idle;
        if (Math.abs(b.currentTime - start) > 0.08) { try { b.currentTime = start; } catch (e) {} }
        b.style.transition = 'none'; b.style.opacity = '0'; b.style.zIndex = '2'; a.style.zIndex = '1';
        let started = false;
        // la mezcla empieza recién cuando el segundo video ya pintó un cuadro (evita el «apagón» en iPhone)
        const go = () => {
          if (started) return; started = true;
          b.style.transition = `opacity ${XF}s ease-in-out`; b.style.opacity = '1';
          st.timer = setTimeout(() => swap(a, b), XF * 1000 + 80);
        };
        if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) b.requestVideoFrameCallback(() => requestAnimationFrame(go));
        else b.addEventListener('playing', () => setTimeout(go, 120), { once: true });
        setTimeout(go, 1500);
        const p = b.play(); if (p && p.catch) p.catch(() => {});
      };
      const prime = () => {
        // carga y decodifica el segundo video en el punto de bucle antes de mezclar (iPhone ignora preload)
        if (st.primed || st.fading) return;
        ensureSrc();
        st.primed = true;
        const b = st.idle;
        try { b.currentTime = start; } catch (e) {}
        const p = b.play();
        if (p && p.then) p.then(() => { if (!st.fading && b === st.idle) { b.pause(); try { b.currentTime = start; } catch (e) {} } }).catch(() => {});
      };
      const watch = (v) => {
        const check = () => {
          if (v !== st.active || v.paused || !isFinite(v.duration)) return;
          const left = v.duration - v.currentTime;
          if (left <= XF + 3) prime();
          if (left <= LEAD) begin();
        };
        v.addEventListener('timeupdate', check);
        if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
          const tick = () => { check(); v.requestVideoFrameCallback(tick); };
          v.requestVideoFrameCallback(tick);
        }
        v.addEventListener('ended', () => { if (v === st.active && !st.fading && slide.classList.contains('is-active')) begin(); });
      };
      watch(video); watch(twin);
      st.reset = () => {
        clearTimeout(st.timer);
        st.fading = false; st.passes = 0; st.primed = false;
        twin.pause(); twin.style.transition = 'none'; twin.style.opacity = '0'; twin.style.zIndex = '';
        video.style.transition = 'none'; video.style.opacity = ''; video.style.zIndex = '';
        st.active = video; st.idle = twin;
      };
    }

    /* Repite el video (sin bucle) cuando el hero vuelve a verse */
    #replay(slide) {
      const video = slide.querySelector('video');
      if (!video || slide.dataset.end === 'loop' || slide.dataset.end === 'loop_tail') return false;
      if (!(video.ended || slide.classList.contains('is-ended') || video.paused)) return false;
      slide.classList.remove('is-ended');
      slide.querySelectorAll('[data-kb], [data-end-image]').forEach((el) => this.#stopKb(el));
      this.#playVideo(slide);
      return true;
    }

    #restartDot() {
      const fill = this.dots[this.index]?.querySelector('.lx-hero__dot-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    #onVideoEnd(slide, video) {
      if (!slide.classList.contains('is-active')) return;
      const end = slide.dataset.end;
      if (end === 'loop_tail' || end === 'loop') return;
      this.style.setProperty('--dot-dur', `${this.speed}ms`);
      if (end === 'next') {
        this.go(this.index + 1);
        return;
      }
      if (end === 'end_image' && slide.querySelector('[data-end-image]')) {
        slide.classList.add('is-ended');
        this.#startKb(slide.querySelector('[data-end-image]'));
      } else {
        // último cuadro congelado + Ken Burns
        video.pause();
        slide.classList.add('is-ended');
        this.#startKb(slide.querySelector('[data-kb]'));
      }
      this.#schedule(this.speed);
    }

    #startKb(el) {
      if (!el || reduceMotion) return;
      el.classList.remove('is-kb-on');
      void el.offsetWidth;
      el.classList.add('is-kb-on');
    }

    #stopKb(el) {
      if (!el) return;
      el.classList.remove('is-kb-on');
    }

    #bindSwipe() {
      let x0 = null;
      let y0 = null;
      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        x0 = e.clientX;
        y0 = e.clientY;
      }, { passive: true });
      this.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      this.addEventListener('mouseenter', () => { this.paused = true; this.classList.add('is-paused'); clearTimeout(this.timer); });
      this.addEventListener('mouseleave', () => {
        this.paused = false;
        this.classList.remove('is-paused');
        const slide = this.slides[this.index];
        const video = slide?.querySelector('video');
        if (slide?.dataset.type === 'video' && video && !video.paused && !video.ended && slide.dataset.end !== 'loop' && !(slide.dataset.end === 'loop_tail' && slide._xf?.passes > 0)) return;
        this.#schedule(this.speed * 0.6);
      });
    }

    #bindVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        const video = this.slides[this.index]?.querySelector('video');
        if (!this.inView) {
          clearTimeout(this.timer);
          const sl = this.slides[this.index];
          const act = sl?._xf?.active || video;
          if (act && !act.paused) { sl.querySelectorAll('video').forEach((v) => v.pause()); act.dataset.wasPlaying = '1'; }
        } else {
          const slide = this.slides[this.index];
          if (slide && slide.dataset.type === 'video' && this.#replay(slide)) { delete video?.dataset.wasPlaying; return; }
          const act = slide?._xf?.active || video;
          if (act && act.dataset.wasPlaying) { act.play().catch(() => {}); delete act.dataset.wasPlaying; }
          else if (slide?.dataset.type === 'video' && video && !video.ended && !slide.classList.contains('is-ended') && slide.dataset.end !== 'loop') { /* el video manda: avanza al terminar */ }
          else this.#schedule(this.speed);
        }
      }, { threshold: 0.2 });
      this.io.observe(this);
    }

    #bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.contains(e.target)) return;
        const idx = this.slides.indexOf(e.target.closest('.lx-hero__slide'));
        this.autoplay = false;
        if (idx > -1) this.activate(idx);
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (!this.contains(e.target)) return;
        this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
        this.#schedule(this.speed);
      });
    }
  }

  customElements.define('lx-hero', LxHero);
})();
